import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { API } from "../Config/API";
import { getDeviceInfo } from "../utils/deviceId";

// Types
export interface User {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  status: "active" | "inactive" | "suspended";
  email_verified: boolean;
  last_login: string | null;
  subscription_end_date: string | null;
  createdAt: string;
  updatedAt: string;
  role_id?: number | null;
  agent_id?: number | string | null;
  agent_code?: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface LoginCredentials {
  identifier: string;
  password: string;
  login_type: "email" | "phone";
}

interface RegisterData {
  full_name: string;
  email: string;
  phone: string;
  password: string;
  confirm_password: string;
}

interface AuthResponse {
  message: string;
  user: User;
  token?: string;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: string }
>("auth/login", async (credentials: any, { rejectWithValue }: any) => {
  try {
    const response = await fetch(`${API.BASE_URL}epaper-users/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: { ...credentials, ...getDeviceInfo() } }),
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data.error?.message || "Login failed");
    }

    return data;
  } catch (error) {
    return rejectWithValue("Network error occurred");
  }
});

export const registerUser = createAsyncThunk<
  AuthResponse,
  RegisterData,
  { rejectValue: string }
>("auth/register", async (userData: any, { rejectWithValue }: any) => {
  try {
    const response = await fetch(`${API.BASE_URL}epaper-users/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: userData }),
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data.error?.message || "Registration failed");
    }

    return data;
  } catch (error) {
    return rejectWithValue("Network error occurred");
  }
});

export const verifyEmail = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>("auth/verifyEmail", async (token: any, { rejectWithValue }: any) => {
  try {
    const response = await fetch(`${API.BASE_URL}epaper-users/verify-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: { token } }),
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(
        data.error?.message || "Email verification failed"
      );
    }

    return data;
  } catch (error) {
    return rejectWithValue("Network error occurred");
  }
});

export const resendVerification = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>("auth/resendVerification", async (email: any, { rejectWithValue }: any) => {
  try {
    const response = await fetch(
      `${API.BASE_URL}epaper-users/resend-verification`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: { email } }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(
        data.error?.message || "Failed to resend verification"
      );
    }

    return data;
  } catch (error) {
    return rejectWithValue("Network error occurred");
  }
});

export const fetchUserProfile = createAsyncThunk<
  { user: User },
  string,
  { rejectValue: string }
>("auth/fetchProfile", async (token: any, { rejectWithValue }: any) => {
  try {
    const response = await fetch(`${API.BASE_URL}epaper-users/profile`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data.error?.message || "Failed to fetch profile");
    }

    return data;
  } catch (error) {
    return rejectWithValue("Network error occurred");
  }
});

export const forgotPassword = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>("auth/forgotPassword", async (email: any, { rejectWithValue }: any) => {
  try {
    const response = await fetch(
      `${API.BASE_URL}epaper-users/forgot-password`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: { email } }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(
        data.error?.message || "Failed to send reset email"
      );
    }

    return data;
  } catch (error) {
    return rejectWithValue("Network error occurred");
  }
});

export const resetPassword = createAsyncThunk<
  { message: string },
  { token: string; password: string; confirmPassword: string },
  { rejectValue: string }
>(
  "auth/resetPassword",
  async ({ token, password, confirmPassword }, { rejectWithValue }: any) => {
    try {
      const response = await fetch(
        `${API.BASE_URL}epaper-users/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: {
              token,
              password,
              confirm_password: confirmPassword,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(
          data.error?.message || "Failed to reset password"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue("Network error occurred");
    }
  }
);

export const changePassword = createAsyncThunk<
  { message: string },
  { currentPassword: string; newPassword: string; confirmPassword: string },
  { rejectValue: string }
>(
  "auth/changePassword",
  async (
    { currentPassword, newPassword, confirmPassword },
    { rejectWithValue, getState }: any
  ) => {
    try {
      const state = getState();
      const token = state.auth.token || localStorage.getItem("authToken");

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const response = await fetch(
        `${API.BASE_URL}epaper-users/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            data: {
              current_password: currentPassword,
              new_password: newPassword,
              confirm_password: confirmPassword,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(
          data.error?.message || "Failed to change password"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue("Network error occurred");
    }
  }
);

export const updateProfile = createAsyncThunk<
  { data: User; message: string },
  { full_name?: string; email?: string; phone?: string },
  { rejectValue: string }
>(
  "auth/updateProfile",
  async (payload, { rejectWithValue, getState }: any) => {
    try {
      const state = getState();
      const token = state.auth.token || localStorage.getItem("authToken");

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const response = await fetch(
        `${API.BASE_URL}epaper-users/update-profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ data: payload }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(
          data.error?.message || "Failed to update profile"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue("Network error occurred");
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state: any) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    clearError: (state: any) => {
      state.error = null;
    },
    setUser: (state: any, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    setToken: (state: any, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    rehydrateAuth: (
      state: any,
      action: PayloadAction<{ user: User; token: string }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder: any) => {
    // Login
    builder
      .addCase(loginUser.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token || null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Login failed";
      });

    // Register
    builder
      .addCase(registerUser.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.error = null;
        // Don't auto-login after registration, user needs to verify email
      })
      .addCase(registerUser.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Registration failed";
      });

    // Email verification
    builder
      .addCase(verifyEmail.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state: any) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Email verification failed";
      });

    // Resend verification
    builder
      .addCase(resendVerification.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendVerification.fulfilled, (state: any) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendVerification.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to resend verification";
      });

    // Fetch profile
    builder
      .addCase(fetchUserProfile.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch profile";
        // If profile fetch fails, user might have invalid token
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });

    // Forgot password
    builder
      .addCase(forgotPassword.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state: any) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to send reset email";
      });

    // Reset password
    builder
      .addCase(resetPassword.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state: any) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to reset password";
      });

    // Change password
    builder
      .addCase(changePassword.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state: any) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to change password";
      });

    // Update profile
    builder
      .addCase(updateProfile.pending, (state: any) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload?.data) {
          state.user = { ...state.user, ...action.payload.data };
          try {
            localStorage.setItem("authUser", JSON.stringify(state.user));
          } catch {}
        }
      })
      .addCase(updateProfile.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update profile";
      });
  },
});

export const { logout, clearError, setUser, setToken, rehydrateAuth } =
  authSlice.actions;
export default authSlice.reducer;
